import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DeleteSweep as BulkDeleteIcon,
  Edit as EditIcon,
  TrendingUp,
  TrendingDown,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { stockService, Stock as BaseStock } from '../../services/stockService';
import { priceTriggersService, PriceTrigger } from '../../services/priceTriggersService';

interface Stock extends BaseStock {
  name?: string;
  last_trade_price?: number;
}

const PriceTriggers: React.FC = () => {
  const [triggers, setTriggers] = useState<PriceTrigger[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState('');
  const [triggerPrice, setTriggerPrice] = useState('');
  const [triggerType, setTriggerType] = useState<'higher' | 'lower'>('higher');
  
  // Edit functionality
  const [editingTrigger, setEditingTrigger] = useState<PriceTrigger | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Stock search optimization
  const [stockSearchInput, setStockSearchInput] = useState('');
  const [filteredStocks, setFilteredStocks] = useState<Stock[]>([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [stocksLoaded, setStocksLoaded] = useState(false);
  const [selectedStockObject, setSelectedStockObject] = useState<Stock | null>(null);
  
  // Bulk operations
  const [selectedTriggers, setSelectedTriggers] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Only load triggers on page load - stocks load on demand when searching
      await loadTriggers();
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTriggers = async () => {
    try {
      const data = await priceTriggersService.getPriceTriggers();
      setTriggers(data.triggers || []);
      return data;
    } catch (error) {
      console.error('Error loading triggers:', error);
      setTriggers([]);
      return { triggers: [] };
    }
  };

  const loadStocks = async () => {
    if (stocksLoaded) return stocks; // Don't reload if already loaded
    
    try {
      setStocksLoading(true);
      console.log('🔍 DEBUG - Loading stocks for search...');
      const data = await stockService.getAllStocks();
      console.log('🔍 DEBUG - StockService call successful, loaded', data?.length, 'stocks');
      setStocks(data || []);
      setStocksLoaded(true);
      return data;
    } catch (error) {
      console.error('Error loading stocks:', error);
      setStocks([]);
      return [];
    } finally {
      setStocksLoading(false);
    }
  };

  // Function to search and filter stocks based on input
  const searchStocks = async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setFilteredStocks([]);
      return;
    }

    // Load stocks if not already loaded
    if (!stocksLoaded) {
      await loadStocks();
    }

    // Filter stocks based on search term (case-insensitive)
    const searchLower = searchTerm.toLowerCase();
    const filtered = stocks.filter(stock => 
      stock.symbol.toLowerCase().includes(searchLower) ||
      (stock.name && stock.name.toLowerCase().includes(searchLower))
    ).slice(0, 50); // Limit to 50 results for performance

    setFilteredStocks(filtered);
  };

  // Handle stock selection and auto-fill price
  const handleStockSelect = (stock: Stock | null) => {
    setSelectedStockObject(stock);
    if (stock) {
      setSelectedStock(stock.symbol);
      // Auto-fill trigger price with latest known price (only if not editing)
      if (stock.last_trade_price && !isEditing) {
        setTriggerPrice(stock.last_trade_price.toString());
      }
    } else {
      setSelectedStock('');
      if (!isEditing) {
        setTriggerPrice('');
      }
    }
  };

  // Handle editing a trigger
  const handleEditTrigger = async (trigger: PriceTrigger) => {
    setEditingTrigger(trigger);
    setIsEditing(true);
    setSelectedStock(trigger.symbol);
    setTriggerPrice(trigger.trigger_price.toString());
    setTriggerType(trigger.trigger_type);
    
    // Load stocks if needed first
    if (!stocksLoaded) {
      await loadStocks();
    }
    
    // Find the current stock and set up the autocomplete properly
    const currentStock = stocks.find(s => s.symbol === trigger.symbol);
    if (currentStock) {
      // Set the selected stock object and filtered stocks
      setSelectedStockObject(currentStock);
      setFilteredStocks([currentStock]);
    } else {
      // Fallback if stock not found in loaded stocks - use trigger data
      const mockStock: Stock = {
        id: 0, // Mock ID since we don't have the actual stock ID
        symbol: trigger.symbol,
        exchange: 'NSE', // Default exchange
        created_at: new Date().toISOString(), // Mock timestamp
        name: trigger.stock_name || undefined,
        last_trade_price: undefined
      };
      setSelectedStockObject(mockStock);
      setFilteredStocks([mockStock]);
    }
    
    setDialogOpen(true);
  };

  const handleCreateTrigger = async () => {
    if (!selectedStock || !triggerPrice) {
      toast.error('Please fill in all fields');
      return;
    }

    const price = parseFloat(triggerPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      setCreating(true);
      
      if (isEditing && editingTrigger) {
        // Update existing trigger
        const response = await priceTriggersService.updatePriceTrigger(editingTrigger.id, {
          symbol: selectedStock,
          trigger_price: price,
          trigger_type: triggerType,
        });

        if (response.success) {
          toast.success('Price trigger updated successfully');
          setDialogOpen(false);
          resetForm();
          loadTriggers();
        } else {
          toast.error(response.message || 'Failed to update trigger');
        }
      } else {
        // Create new trigger
        const response = await priceTriggersService.createPriceTrigger({
          symbol: selectedStock,
          trigger_price: price,
          trigger_type: triggerType,
        });

        if (response.success) {
          toast.success('Price trigger created successfully');
          setDialogOpen(false);
          resetForm();
          loadTriggers();
        } else {
          toast.error(response.message || 'Failed to create trigger');
        }
      }
    } catch (error: any) {
      console.error('Error saving trigger:', error);
      toast.error(error.response?.data?.message || 'Failed to save trigger');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleTrigger = async (triggerId: string) => {
    try {
      const response = await priceTriggersService.togglePriceTrigger(triggerId);
      
      if (response.success) {
        toast.success(response.message || 'Trigger status updated successfully');
        loadTriggers();
      } else {
        toast.error(response.message || 'Failed to update trigger status');
      }
    } catch (error: any) {
      console.error('Error toggling trigger:', error);
      toast.error(error.response?.data?.message || 'Failed to update trigger status');
    }
  };

  const handleDeleteTrigger = async (triggerId: string) => {
    try {
      const response = await priceTriggersService.deletePriceTrigger(triggerId);
      
      if (response.success) {
        toast.success('Trigger deleted successfully');
        loadTriggers();
      } else {
        toast.error(response.message || 'Failed to delete trigger');
      }
    } catch (error: any) {
      console.error('Error deleting trigger:', error);
      toast.error(error.response?.data?.message || 'Failed to delete trigger');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTriggers.length === 0) {
      toast.error('No triggers selected');
      return;
    }

    try {
      setBulkDeleting(true);
      
      const response = await priceTriggersService.bulkDeletePriceTriggers(selectedTriggers);

      if (response.success) {
        toast.success(`${response.deleted_count} triggers deleted successfully`);
        setSelectedTriggers([]);
        loadTriggers();
      } else {
        toast.error(response.message || 'Failed to delete triggers');
      }
    } catch (error: any) {
      console.error('Error bulk deleting triggers:', error);
      toast.error(error.response?.data?.message || 'Failed to delete triggers');
    } finally {
      setBulkDeleting(false);
    }
  };

  const resetForm = () => {
    setSelectedStock('');
    setTriggerPrice('');
    setTriggerType('higher');
    setStockSearchInput('');
    setFilteredStocks([]);
    setSelectedStockObject(null);
    setEditingTrigger(null);
    setIsEditing(false);
  };

  const handleTriggerSelection = (triggerId: string) => {
    setSelectedTriggers(prev => 
      prev.includes(triggerId) 
        ? prev.filter(id => id !== triggerId)
        : [...prev, triggerId]
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getSelectedStock = () => {
    return stocks.find(stock => stock.symbol === selectedStock);
  };

  const getTriggerStatus = (trigger: PriceTrigger) => {
    if (!trigger.is_active) return 'disabled';
    if (!trigger.current_price) return 'unknown';
    
    if (trigger.trigger_type === 'higher') {
      return trigger.current_price >= trigger.trigger_price ? 'triggered' : 'active';
    } else {
      return trigger.current_price <= trigger.trigger_price ? 'triggered' : 'active';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" gap={2}>
          {selectedTriggers.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BulkDeleteIcon />}
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              title="Delete Selected Triggers"
            >
              ({selectedTriggers.length})
            </Button>
          )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              title="Add Trigger"
            >
            </Button>
        </Box>
      </Box>

      {/* Triggers Grid */}
      {triggers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No Price Triggers Set
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
              Create your first price trigger to get notified when stocks reach your target levels
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Your First Trigger
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {triggers.map((trigger) => {
            const status = getTriggerStatus(trigger);
            const isSelected = selectedTriggers.includes(trigger.id);
            
            return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={trigger.id}>
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="caption" display="block">
                        {trigger.stock_name || trigger.symbol}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Current: {formatCurrency(trigger.current_price || 0)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Target: {formatCurrency(trigger.trigger_price)} ({trigger.trigger_type})
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <Card 
                    variant="outlined"
                    sx={{ 
                      position: 'relative',
                      border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      bgcolor: status === 'triggered' ? 'success.50' : 'background.paper',
                      opacity: trigger.is_active ? 1 : 0.6,
                    }}
                  >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      {/* Header: Symbol + Checkbox */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ maxWidth: '75%' }}>
                          {trigger.symbol.replace('.NS', '')}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleTriggerSelection(trigger.id)}
                          sx={{ p: 0 }}
                        />
                      </Box>
                      
                      {/* Prices: Current vs Target in one row */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <Box display="flex" alignItems="center">
                          {trigger.trigger_type === 'higher' ? (
                            <TrendingUp sx={{ fontSize: 14, color: 'success.main', mr: 0.25 }} />
                          ) : (
                            <TrendingDown sx={{ fontSize: 14, color: 'error.main', mr: 0.25 }} />
                          )}
                          <Typography 
                            variant="caption" 
                            fontWeight="bold"
                            color={trigger.trigger_type === 'higher' ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(trigger.trigger_price)}
                          </Typography>
                        </Box>
                        {trigger.current_price && (
                          <Typography variant="caption" color="text.secondary">
                            @{formatCurrency(trigger.current_price)}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Actions row */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Chip
                          label={status === 'disabled' ? 'Off' : status === 'triggered' ? '✓' : 'On'}
                          color={status === 'disabled' ? 'default' : status === 'triggered' ? 'success' : 'primary'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                        <Box display="flex" gap={0}>
                          <IconButton
                            size="small"
                            color={trigger.is_active ? "warning" : "default"}
                            onClick={() => handleToggleTrigger(trigger.id)}
                            sx={{ p: 0.25 }}
                            title="Toggle Trigger"
                          >
                            <NotificationsIcon sx={{ fontSize: 16, opacity: trigger.is_active ? 1 : 0.4 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditTrigger(trigger)}
                            sx={{ p: 0.25 }}
                            title="Edit Trigger"
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteTrigger(trigger.id)}
                            sx={{ p: 0.25 }}
                            title="Delete Trigger"
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Trigger Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Price Trigger' : 'Create Price Trigger'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Stock Selection */}
            <Autocomplete
              fullWidth
              options={filteredStocks}
              value={selectedStockObject}
              getOptionLabel={(option) => option.name ? `${option.symbol} - ${option.name}` : option.symbol}
              isOptionEqualToValue={(option, value) => option.symbol === value.symbol}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {option.symbol}
                      </Typography>
                      {option.name && (
                        <Typography variant="caption" color="textSecondary">
                          {option.name}
                        </Typography>
                      )}
                      {option.last_trade_price && (
                        <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                          ₹{option.last_trade_price.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              inputValue={stockSearchInput}
              onInputChange={(event, newInputValue) => {
                setStockSearchInput(newInputValue);
                searchStocks(newInputValue);
              }}
              onChange={(event, newValue) => {
                handleStockSelect(newValue);
              }}
              loading={stocksLoading}
              loadingText="Loading stocks..."
              noOptionsText={
                stockSearchInput.length < 3 
                  ? "Type at least 3 characters to search stocks" 
                  : "No stocks found"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Stock"
                  margin="normal"
                  helperText="Type at least 3 characters to search"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {stocksLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Current Price Info */}
            {selectedStock && (
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                Selected stock: <strong>{selectedStock}</strong>
                {getSelectedStock()?.last_trade_price && (
                  <span> - Current price: {formatCurrency(getSelectedStock()!.last_trade_price!)}</span>
                )}
              </Alert>
            )}

            {/* Trigger Type */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Trigger When Price Goes</InputLabel>
              <Select
                value={triggerType}
                onChange={(e) => setTriggerType(e.target.value as 'higher' | 'lower')}
                label="Trigger When Price Goes"
              >
                <MenuItem value="higher">
                  <Box display="flex" alignItems="center">
                    <TrendingUp color="success" sx={{ mr: 1 }} />
                    Higher Than
                  </Box>
                </MenuItem>
                <MenuItem value="lower">
                  <Box display="flex" alignItems="center">
                    <TrendingDown color="error" sx={{ mr: 1 }} />
                    Lower Than
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Trigger Price */}
            <TextField
              fullWidth
              margin="normal"
              label="Trigger Price (₹)"
              type="number"
              value={triggerPrice}
              onChange={(e) => setTriggerPrice(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Enter the price level that will trigger the notification"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button 
            onClick={handleCreateTrigger} 
            variant="contained"
            disabled={creating || !selectedStock || !triggerPrice}
          >
            {creating ? <CircularProgress size={20} /> : (isEditing ? 'Update Trigger' : 'Create Trigger')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceTriggers;
